import React, { Suspense } from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import { LastLocationProvider } from 'react-router-last-location'

import { AIAssistantComponent } from 'components/AIAssistantComponent'
import { AIAssistantProvider } from 'components/AIAssistantComponent/providers/AIAssistantProvider'
import { ClosedAccount } from 'pages/closed-account'
import { PricingInterstitial } from 'pages/pricing-interstitial'
import { RecapPage } from 'pages/publishing/RecapPage'

import ConnectionProblems from '../pages/connection-problems'
import SuspendedUploadPage from '../pages/document-uploader-suspension'
import Loading from '../pages/loading'
import RedirectToMobile from '../pages/redirect-to-mobile'
import LockedAccount from '../pages/spend-locked-account'
import GoogleAnalytics from '../utils/analytics/GoogleAnalytics'
import withAuthentication from '../utils/authentication'
import RestoreScrollPosition from '../utils/RestoreScrollPosition'
import SetPageTitle from '../utils/SetPageTitle'
import routes from '../wv/routes'
import AuthenticatedProviders from './AuthenticatedProviders'
import { getUserConfirmation } from './getUserConfirmation'
import { PublishingArticle, usePreloadLazyComponents } from './lazy'
import * as mainRoutes from './routes'

const AuthenticatedRoutes = withAuthentication({
  loading: Loading,
  error: ConnectionProblems,
  closedAccount: ClosedAccount,
  lockedSpendAccount: LockedAccount,
  redirectToMobileApp: RedirectToMobile,
  pricingInterstitial: PricingInterstitial,
})(({ asyncComponent }) => <>{asyncComponent}</>)

const MainRouter = () => {
  const trackedGetUserConfirmation = (
    message: string,
    callback: (ok: boolean) => void,
  ) => {
    getUserConfirmation(message, callback)
  }

  // Preloads all code split bundles
  usePreloadLazyComponents(1000)

  return (
    <BrowserRouter getUserConfirmation={trackedGetUserConfirmation}>
      <AIAssistantProvider>
        <LastLocationProvider>
          <RestoreScrollPosition>
            <SetPageTitle />
            {/* Note: Track all authenticated and unauthenticated page views to GA */}
            <GoogleAnalytics />

            <Switch>
              {/* For pages don't require authentication or require no authentication */}
              {routes.map((props: any, i: number) => (
                <Route key={i} {...props} />
              ))}

              {/* FAMILY PUBLISHING ARTICLE - UNAUTHENTICATED */}
              <Route
                exact={true}
                path={mainRoutes.routes.publishing.recap.public.article}
              >
                <Suspense fallback={<Loading />}>
                  <PublishingArticle />
                </Suspense>
              </Route>

              {/* FAMILY PUBLISHING BENEFICIARY RECAP - UNAUTHENTICATED */}
              <Route
                path={mainRoutes.routes.publishing.recap.public.main}
                component={RecapPage}
              />

              {/* Document Upload Page for Suspended Users - UNAUTHENTICATED*/}
              <Route path="/document-uploader-suspension">
                <SuspendedUploadPage />
              </Route>

              {/* For all other pages that must be behind authentication */}
              <AuthenticatedProviders>
                <AuthenticatedRoutes />
              </AuthenticatedProviders>
            </Switch>
          </RestoreScrollPosition>
        </LastLocationProvider>
        <AIAssistantComponent />
      </AIAssistantProvider>
    </BrowserRouter>
  )
}

// Note: This component binds top-level 'page' components to various URL paths
export default MainRouter
